export interface AnonymousChallengeConfig {
  provider: "turnstile";
  siteKey: string;
  action: string;
}

type ChallengeListener = (challenge: AnonymousChallengeConfig | null) => void;

interface ActiveChallenge {
  config: AnonymousChallengeConfig;
  promise: Promise<string>;
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}

const listeners = new Set<ChallengeListener>();
let activeChallenge: ActiveChallenge | null = null;

function notifyListeners() {
  for (const listener of listeners) {
    listener(activeChallenge?.config ?? null);
  }
}

export function subscribeToAnonymousChallenge(listener: ChallengeListener) {
  listeners.add(listener);
  listener(activeChallenge?.config ?? null);
  return () => {
    listeners.delete(listener);
  };
}

export function requestAnonymousChallenge(config: AnonymousChallengeConfig): Promise<string> {
  if (!config.siteKey || config.provider !== "turnstile") {
    return Promise.reject(new Error("Human verification is not configured correctly."));
  }
  if (activeChallenge) {
    return activeChallenge.promise;
  }

  let resolveChallenge!: (token: string) => void;
  let rejectChallenge!: (error: Error) => void;
  const promise = new Promise<string>((resolve, reject) => {
    resolveChallenge = resolve;
    rejectChallenge = reject;
  });
  activeChallenge = {
    config,
    promise,
    resolve: resolveChallenge,
    reject: rejectChallenge,
  };
  notifyListeners();
  return promise;
}

export function completeAnonymousChallenge(token: string) {
  const challenge = activeChallenge;
  if (!challenge) return;
  activeChallenge = null;
  challenge.resolve(token);
  notifyListeners();
}

export function cancelAnonymousChallenge(message = "Verification was cancelled.") {
  const challenge = activeChallenge;
  if (!challenge) return;
  activeChallenge = null;
  challenge.reject(new Error(message));
  notifyListeners();
}
